import { createStyles, makeStyles } from "@material-ui/styles";
import {
    DateField,
    DialogActions,
    DialogContent,
    DialogTitle,
    domain,
    Label,
    Layout,
    LayoutCell,
    NumericField,
    RelodeApiError,
    RelodeTheme,
    SelectableButtonItem,
    SelectableButtonsList,
    Text,
    useNotifications,
} from "@relode/core";
import { Form, useFormikContext } from "formik";
import * as React from "react";
import { Prompt } from "react-router";

import { CandidateJobDetails, HiredCandidate } from "../../types/";
import {
    HiredCandidateDialogState,
    HiredCandidateDialogStatus,
    HiredCandidateType,
} from "./hiredCandidateDialogProps";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            section: {
                display: "flex",
                flexGrow: 1,
                flexDirection: "column",
                marginBottom: theme.spacing(2),
                marginTop: theme.spacing(2),
            },
            datesSection: {
                display: "flex",
                flexGrow: 1,
                marginBottom: theme.spacing(2),
                [theme.breakpoints.down("sm")]: {
                    flexDirection: "column",
                },
            },
            form: {
                display: "flex",
                flexGrow: 1,
                flexDirection: "column",
            },
            input: {
                margin: 0,
                "&:not(:first-child)": {
                    marginLeft: 0,
                    marginTop: theme.spacing(2),
                },
                [theme.breakpoints.up("md")]: {
                    "&:not(:first-child)": {
                        marginLeft: theme.spacing(3),
                        marginTop: 0,
                    },
                },
            },
            stakeholder: {
                display: "inline-flex",
            },
            salaryTypeList: {
                paddingTop: "6px",
                paddingBottom: "7px",
            },
        }),
    { name: "HiredCandidateForm" }
);

const salaryTypes = [
    {
        label: domain.JobSalaryTypeName.PerYear,
        value: domain.JobSalaryTypeEnum.PerYear,
    },
    {
        label: domain.JobSalaryTypeName.PerHour,
        value: domain.JobSalaryTypeEnum.PerHour,
    },
];

interface Props {
    candidateName: string;
    job: CandidateJobDetails;
    onClose: () => void;
    onComplete: () => void;
    onSubmit: HiredCandidateType;
    showUserAttributions?: boolean;
}

export const HiredCandidateForm = (props: Props) => {
    const formik = useFormikContext<HiredCandidate>();
    const { candidateName, job, onClose, onSubmit, onComplete } = props;
    const [state, setState] = React.useState<HiredCandidateDialogState>({
        error: undefined,
        status: "unset",
        isBusy: false,
    });

    const {
        errors,
        dirty,
        values,
        touched,
        setFieldValue,
        setFieldError,
        setFieldTouched,
        setSubmitting,
    } = formik;

    const {
        hiredJobSalaryInUSDollars,
        hiredStartDate,
        hiredEndDate,
        hiredJobSalaryTypeId,
        hiredHoursPerWeek,
    } = values;

    const classes = useStyles({});
    const { actions: notifications } = useNotifications();
    const isSalaryTypePerHour = domain.JobSalaryType.isPerHour(
        hiredJobSalaryTypeId
    );

    React.useEffect(() => {
        switch (state.status) {
            // NOTE: DO NOT SET ANY STATE HERE. WILL CAUSE LOOP
            case "pending": {
                notifications.closeAreaNotification("hireErrorArea");
                break;
            }

            case "failure": {
                if (!state.error) return;
                if (
                    state.error.validationErrors &&
                    state.error.validationErrors.length > 0
                ) {
                    state.error.validationErrors.forEach(err => {
                        const message = err.messages.join(" ");
                        setFieldError(
                            err.field as keyof HiredCandidate,
                            message
                        );
                        setFieldTouched(err.field as keyof HiredCandidate);
                    });
                }

                if (state.error.summary) {
                    notifications.showArea(
                        "error",
                        state.error.summary,
                        "hireErrorArea",
                        {
                            canClose: false,
                        }
                    );
                }
                break;
            }

            case "success": {
                notifications.closeAreaNotification("hireErrorArea");
                onComplete();
                break;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state, setFieldError, setFieldTouched]);

    const setHiredStatus = (
        status: HiredCandidateDialogStatus,
        error: RelodeApiError | undefined
    ) => {
        setState(o => ({ ...o, status, error, isBusy: status === "pending" }));
    };

    const handleSubmit = async () => {
        setHiredStatus("pending", undefined);
        await onSubmit(values, setHiredStatus);
        setSubmitting(false);
    };

    const handleButtonFieldChange = (field: string) => (
        item: SelectableButtonItem
    ) => {
        setFieldValue(field as keyof HiredCandidate, item.id);
        setFieldTouched(field as keyof HiredCandidate);
    };

    return (
        <>
            <Prompt
                when={dirty}
                message="You have unsaved changes, are you sure you want to leave?"
            />
            <DialogTitle title="hire details" onClose={onClose} />
            <DialogContent notificationAreaName="hireErrorArea">
                <Text variant="h4">{candidateName}</Text>
                <Form className={classes.form}>
                    <Layout mode="50/50">
                        <LayoutCell columns={2}>
                            <NumericField
                                fullWidth={true}
                                label={
                                    job.jobType.isContractOrLocum()
                                        ? "hourly rate"
                                        : job.jobType.payLabel
                                }
                                disabled={job.jobType.isContractOrLocum()}
                                id="hiredJobSalaryInUSDollars"
                                data-cy="hiredJobSalaryInUSDollars"
                                name="hiredJobSalaryInUSDollars"
                                startAdornment="dollar"
                                allowDecimal={true}
                            />
                        </LayoutCell>
                        <LayoutCell columns={2}>
                            <NumericField
                                fullWidth={true}
                                className={classes.input}
                                label="hours per week"
                                id="hiredHoursPerWeek"
                                data-cy="hiredHoursPerWeek"
                                name="hiredHoursPerWeek"
                            />
                        </LayoutCell>
                        <LayoutCell columns={2}>
                            <Label label="salary type" fullWidth>
                                <SelectableButtonsList
                                    id="salaryType"
                                    data-cy="salaryType"
                                    className={classes.salaryTypeList}
                                    onClick={handleButtonFieldChange(
                                        "hiredJobSalaryTypeId"
                                    )}
                                    disabled={job.jobType.isContractOrLocum()}
                                    items={salaryTypes.map(salaryType => ({
                                        id: salaryType.value,
                                        label: salaryType.label,
                                        selected:
                                            salaryType.value ===
                                            hiredJobSalaryTypeId,
                                    }))}
                                    error={!!errors["hiredJobSalaryTypeId"]}
                                    helperText={errors["hiredJobSalaryTypeId"]}
                                />
                            </Label>
                        </LayoutCell>
                        <DateField
                            name="hiredStartDate"
                            label="start date"
                            data-cy="hiredStartDate"
                        />
                        {job.jobType.isContractOrLocum() ? (
                            <DateField
                                name="hiredEndDate"
                                data-cy="hiredEndDate"
                                label="end date"
                            />
                        ) : null}
                    </Layout>
                </Form>
            </DialogContent>
            <DialogActions
                submit={{
                    onSubmit: handleSubmit,
                    disabled: !formik.isValid || state.isBusy,
                }}
            />
        </>
    );
};

HiredCandidateForm.displayName = "HiredCandidateForm";
